import { Flex, HStack } from '@chakra-ui/react';
import React from 'react';

import type { Transaction } from 'types/api/transaction';

import config from 'configs/app';
import getValueWithUnit from 'lib/getValueWithUnit';
import { currencyUnits } from 'lib/units';
import { Skeleton } from 'toolkit/chakra/skeleton';
import { space } from 'toolkit/utils/htmlEntities';
import AddressFromTo from 'ui/shared/address/AddressFromTo';
import BlockEntity from 'ui/shared/entities/block/BlockEntity';
import TxEntity from 'ui/shared/entities/tx/TxEntity';
import ListItemMobile from 'ui/shared/ListItemMobile/ListItemMobile';
import TxStatus from 'ui/shared/statusTag/TxStatus';
import TimeWithTooltip from 'ui/shared/time/TimeWithTooltip';
import TxFee from 'ui/shared/tx/TxFee';
import TxWatchListTags from 'ui/shared/tx/TxWatchListTags';
import TxAdditionalInfo from 'ui/txs/TxAdditionalInfo';
import TxType from 'ui/txs/TxType';

import TxTranslationType from './TxTranslationType';
import TxTypeWithStatus from './TxTypeWithStatus';

type Props = {
  tx: Transaction;
  showBlockInfo: boolean;
  currentAddress?: string;
  enableTimeIncrement?: boolean;
  isLoading?: boolean;
  animation?: string;
  mainPageTable?: boolean;
};

const TxsListItem = ({
  tx,
  isLoading,
  showBlockInfo,
  currentAddress,
  enableTimeIncrement,
  animation,
  mainPageTable,
}: Props) => {
  const dataTo = tx.to ? tx.to : tx.created_contract;
  const type = (() => {
    if (mainPageTable) {
      return (
        <TxTypeWithStatus
          types={ tx.transaction_types }
          status={ tx.status }
          errorText={ tx.status === 'error' ? tx.result : undefined }
          isLoading={ isLoading }
        />
      );
    }

    if (tx.translation) {
      return (
        <TxTranslationType
          types={ tx.transaction_types }
          isLoading={ isLoading || tx.translation.isLoading }
          translatationType={ tx.translation.data?.type }
        />
      );
    }

    return <TxType types={ tx.transaction_types } isLoading={ isLoading }/>;
  })();

  return (
    <ListItemMobile display="block" width="100%" animation={ animation } key={ tx.hash }>
      <Flex justifyContent="space-between" alignItems="flex-start" mt={ 4 }>
        <HStack flexWrap="wrap">
          { type }
          { !mainPageTable && (
            <TxStatus status={ tx.status } errorText={ tx.status === 'error' ? tx.result : undefined } isLoading={ isLoading }/>
          ) }
          { !mainPageTable && <TxWatchListTags tx={ tx } isLoading={ isLoading }/> }
        </HStack>
        { !mainPageTable && <TxAdditionalInfo tx={ tx } isMobile isLoading={ isLoading }/> }
      </Flex>
      <Flex justifyContent="space-between" lineHeight="24px" mt={ 3 } alignItems="center">
        <TxEntity
          isLoading={ isLoading }
          hash={ tx.hash }
          truncation="constant_long"
          fontWeight="700"
          noIcon={ mainPageTable }
          icon={ mainPageTable ? undefined : {
            name: tx.transaction_types.includes('blob_transaction') ? 'blob' : undefined,
          } }
        />
        { !mainPageTable && (
          <TimeWithTooltip
            timestamp={ tx.timestamp }
            enableIncrement={ enableTimeIncrement }
            isLoading={ isLoading }
            color="text.secondary"
            fontWeight="400"
            fontSize="sm"
          />
        ) }
      </Flex>
      { !mainPageTable && tx.method && (
        <Flex mt={ 3 }>
          <Skeleton loading={ isLoading } display="inline-block" whiteSpace="pre">متد </Skeleton>
          <Skeleton
            loading={ isLoading }
            color="text.secondary"
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
          >
            <span>{ tx.method }</span>
          </Skeleton>
        </Flex>
      ) }
      { showBlockInfo && tx.block_number !== null && (
        <Flex mt={ 2 }>
          <Skeleton loading={ isLoading } display="inline-block" whiteSpace="pre">بلاک </Skeleton>
          <BlockEntity
            isLoading={ isLoading }
            number={ tx.block_number }
            noIcon
          />
        </Flex>
      ) }
      <AddressFromTo
        from={ tx.from }
        to={ dataTo }
        current={ currentAddress }
        isLoading={ isLoading }
        mt={ 6 }
        fontWeight="500"
        noIcon={ mainPageTable }
      />
      { !config.UI.views.tx.hiddenFields?.value && (
        <Flex mt={ 2 } columnGap={ 2 }>
          <Skeleton loading={ isLoading } display="inline-block" whiteSpace="pre">مقدار</Skeleton>
          <Skeleton loading={ isLoading } display="inline-block" color="text.secondary" whiteSpace="pre">
            <span>
              { getValueWithUnit(tx.value).toFormat() }
              { space }
              { currencyUnits.ether }
            </span>
          </Skeleton>
        </Flex>
      ) }
      { !mainPageTable && !config.UI.views.tx.hiddenFields?.tx_fee && (
        <Flex mt={ 2 } mb={ 3 } columnGap={ 2 }>
          { (tx.stability_fee !== undefined || tx.fee.value !== null) && (
            <>
              <Skeleton loading={ isLoading } display="inline-block" whiteSpace="pre">کارمزد</Skeleton>
              <TxFee tx={ tx } isLoading={ isLoading }/>
            </>
          ) }
        </Flex>
      ) }
    </ListItemMobile>
  );
};

export default React.memo(TxsListItem);
